import { useState, useEffect, Fragment, useContext } from 'react';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';
import { AuthContext } from '../../../context/AuthProvider';

import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  TextField,
  Typography,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ImageList,
  ImageListItem,
  Avatar,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';

// Icons
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import ImageIcon from '@mui/icons-material/Image';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CollectionsIcon from '@mui/icons-material/Collections';

// Utils
import messages from '../../../static/messages';

// Services
import proposalSystemsServices from '../../../services/proposalSystemsServices';
import proposalOptionServices from '../../../services/proposalOptionServices';
import equipmentServices from '../../../services/equipmentServices';
import warrantyServices from '../../../services/warrantyServices';
import scopeOfWorkServices from '../../../services/scopeOfWorkServices';
import equipmentTypesServices from '../../../services/equipmentTypesServices';

// Drag and Drop
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';

// Components
import { EquipmentSelectModal } from '../../index';

// External variables
import { button } from '../../../static/theme-styles';

// DnD Components
const DragHandle = SortableHandle(() => (
  <DragIndicatorIcon sx={{ cursor: 'grab', color: '#999', '&:hover': { color: '#333' } }} />
));

const SortableTableRow = SortableElement(({ children, ...props }) => (
  <TableRow {...props}>{children}</TableRow>
));

const SortableTableBody = SortableContainer(({ children }) => (
  <TableBody>{children}</TableBody>
));

const ProposalSystemTableNew = ({ proposal, id, refresh }) => {
  const { userData } = useContext(AuthContext);
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState([]);
  const [editingSystem, setEditingSystem] = useState(null);
  const [editingSystemData, setEditingSystemData] = useState({ name: '' });
  const [newSystemName, setNewSystemName] = useState('');
  const [showAddSystem, setShowAddSystem] = useState(false);
  const [equipmentFilters, setEquipmentFilters] = useState(null);
  const [equipmentTypes, setEquipmentTypes] = useState({});
  const [addingEquipment, setAddingEquipment] = useState(null);
  const [equipmentModalSystemId, setEquipmentModalSystemId] = useState(null);
  const [editingEquipmentData, setEditingEquipmentData] = useState(null); // {systemId, equipmentId, type, make, model, price, imageUrl}
  const [newEquipment, setNewEquipment] = useState({ type: '', make: '', model: '', price: '', addPicture: true, imageUrl: null, description: '' });
  
  // Image selection dialog state
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageDialogImages, setImageDialogImages] = useState([]);
  const [imageDialogTarget, setImageDialogTarget] = useState(null); // "new" or {systemId, equipmentId}
  
  // Warranty states
  const [warrantyList, setWarrantyList] = useState([]);
  const [addingWarranty, setAddingWarranty] = useState(null);
  const [selectedWarranty, setSelectedWarranty] = useState('');

  // Scope of Work states
  const [scopeOfWorkList, setScopeOfWorkList] = useState([]);
  const [addingScopeOfWork, setAddingScopeOfWork] = useState(null);
  const [selectedScopeOfWork, setSelectedScopeOfWork] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const confirm = useConfirm();

  // Load systems
  const loadSystems = async () => {
    try {
      setLoading(true);
      const response = await proposalSystemsServices.getSystems(id);
      if (response.status === 200) {
        setSystems(response.data);
      }
    } catch (error) {
      toast.error(messages.errors.error_data_loaning);
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  // Load equipment filters
  const loadEquipmentFilters = async () => {
    try {
      const response = await equipmentServices.getEquipmentKit();
      if (response.status === 200) {
        setEquipmentFilters(response.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Load equipment types with their default images
  const loadEquipmentTypes = async () => {
    try {
      const response = await equipmentTypesServices.getAll();
      if (response.status === 200) {
        const typesMap = {};
        response.data.forEach(type => {
          typesMap[type.name] = { imageUrl: type.imageUrl || null, images: type.images || [] };
        });
        setEquipmentTypes(typesMap);
      }
    } catch (error) {
      console.log(error);
    }
  };


  // Open image selection dialog
  const openImageDialog = (target, typeName) => {
    if (typeName && equipmentTypes[typeName]) {
      const typeData = equipmentTypes[typeName];
      const images = typeData.images || [];
      if (images.length > 0) {
        setImageDialogImages(images);
        setImageDialogTarget(target);
        setImageDialogOpen(true);
      } else {
        console.log('No images available for type:', typeName);
      }
    }
  };

  // Handle image selection from dialog
  const handleSelectImage = async (imageUrl) => {
    if (imageDialogTarget === 'new') {
      setNewEquipment({ ...newEquipment, imageUrl });
    } else if (imageDialogTarget?.systemId && imageDialogTarget?.equipmentId) {
      try {
        await proposalSystemsServices.updateEquipment(
          id,
          imageDialogTarget.systemId,
          imageDialogTarget.equipmentId,
          { imageUrl }
        );
        loadSystems();
      } catch (error) {
        console.log('Error updating equipment image:', error);
      }
    }
    setImageDialogOpen(false);
    setImageDialogTarget(null);
    setImageDialogImages([]);
  };

  // Load warranty list
  const loadWarrantyList = async () => {
    try {
      const response = await warrantyServices.getAllWarranty(0, 1000000, false);
      if (response.status === 206 || response.status === 200) {
        setWarrantyList(response.data.data || []);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Load scope of work list
  const loadScopeOfWorkList = async () => {
    try {
      const response = await scopeOfWorkServices.getAllWorks(0, 1000000, false);
      if (response.status === 206 || response.status === 200) {
        setScopeOfWorkList(response.data.data || []);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Load options for PRO mode
  const loadOptions = async () => {
    try {
      const response = await proposalOptionServices.getOptions(id);
      if (response.status === 200) {
        setOptions(response.data || []);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Add option (PRO mode)
  const handleAddOption = async () => {
    try {
      const response = await proposalOptionServices.createOption(id);
      if (response.status === 200) {
        setOptions(response.data || []);
        loadSystems();
        if (refresh) refresh();
        toast.success('Option added');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || messages.errors.error_try_again);
      console.log(error);
    }
  };

  // Delete option (PRO mode)
  const handleDeleteOption = async (optionId) => {
    confirm({
      title: 'Delete Option',
      description: 'Are you sure? All systems in this option will be deleted.',
    })
      .then(async () => {
        try {
          const response = await proposalOptionServices.deleteOption(id, optionId);
          if (response.status === 200) {
            setOptions(response.data || []);
            loadSystems();
            if (refresh) refresh();
            toast.success('Option deleted');
          }
        } catch (error) {
          toast.error(error.response?.data?.message || messages.errors.error_try_again);
          console.log(error);
        }
      })
      .catch(() => toast.info(messages.cancel_message));
  };

  const isProUser = userData?.proVersion === true;
  const hasOptions = options.length > 0;
  const optionAccepted = proposal?.optionAccepted === true;

  // Add new system
  const handleAddSystem = async (optionId = null) => {
    if (!newSystemName.trim()) {
      toast.error('Please enter a system name');
      return;
    }

    try {
      const data = {
        name: newSystemName,
        installationPrice: 0,
      };
      if (optionId) {
        data.optionId = optionId;
      }
      const response = await proposalSystemsServices.createSystem(id, data);
      if (response.status === 200) {
        toast.success('System added successfully');
        setNewSystemName('');
        setShowAddSystem(false);
        loadSystems();
        if (hasOptions) loadOptions();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  // Update system
  const handleUpdateSystem = async (systemId, data) => {
    try {
      const response = await proposalSystemsServices.updateSystem(id, systemId, data);
      if (response.status === 200) {
        toast.success('System updated');
        setEditingSystem(null);
        setEditingSystemData({ name: '' });
        loadSystems();
        if (refresh) {
          refresh();
        }
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  // Delete system
  const handleDeleteSystem = async (system) => {
    confirm({
      title: messages.confirm_message.remove,
      description: messages.confirm_message.remove_description(`"${system.name}"`),
    })
      .then(async () => {
        try {
          const response = await proposalSystemsServices.deleteSystem(id, system.id);
          if (response.status === 200) {
            toast.success('System deleted');
            loadSystems();
          }
        } catch (error) {
          toast.error(messages.errors.error_try_again);
          console.log(error);
        }
      })
      .catch(() => toast.info(messages.cancel_message));
  };

  // Copy system
  const handleCopySystem = async (system) => {
    confirm({
      title: messages.confirm_message.copy,
      description: messages.confirm_message.copy_description(`"${system.name}"`),
    })
      .then(async () => {
        try {
          const copyData = {
            name: `${system.name} COPY`,
            installationPrice: system.installationPrice,
            notes: system.notes,
            rebates: system.rebates || 0,
            instants: system.instants || 0,
            warranties: system.warranties || [],
            scopeOfWorks: system.scopeOfWorks || [],
          };
          if (system.optionId) {
            copyData.optionId = system.optionId;
          }
          const response = await proposalSystemsServices.createSystem(id, copyData);
          
          if (response.status === 200) {
            const newSystemId = response.data.id;
            
            if (system.equipments && system.equipments.length > 0) {
              for (const eq of system.equipments) {
                await proposalSystemsServices.addEquipment(id, newSystemId, {
                  modelNumber: eq.modelNumber,
                  equipmentName: eq.equipmentName,
                  equipmentType: eq.equipmentType,
                  manufacturerName: eq.manufacturerName,
                  priceAtCreation: eq.priceAtCreation,
                  imageUrl: eq.imageUrl,
                });
              }
            }
            
            toast.success('System copied');
            loadSystems();
          }
        } catch (error) {
          toast.error(messages.errors.error_try_again);
          console.log(error);
        }
      })
      .catch(() => toast.info(messages.cancel_message));
  };

  // Add equipment to system
  const handleAddEquipment = async (systemId) => {
    if (!newEquipment.type || !newEquipment.model) {
      toast.error('Please select equipment type and model');
      return;
    }

    try {
      const equipmentName = `${newEquipment.type} ${newEquipment.make} ${newEquipment.model}`.trim();
      
      let finalImageUrl = null;
      if (newEquipment.addPicture) {
        finalImageUrl = newEquipment.imageUrl || equipmentTypes[newEquipment.type]?.imageUrl || null;
      }
      
      const response = await proposalSystemsServices.addEquipment(id, systemId, {
        modelNumber: newEquipment.model,
        equipmentName: equipmentName,
        equipmentType: newEquipment.type,
        manufacturerName: newEquipment.make,
        priceAtCreation: parseFloat(newEquipment.price) || 0,
        imageUrl: finalImageUrl,
        description: newEquipment.description,
      });
      
      if (response.status === 200) {
        toast.success('Equipment added');
        setAddingEquipment(null);
        setNewEquipment({ type: '', make: '', model: '', price: '', addPicture: true, imageUrl: null, description: '' });
        loadSystems();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  // Add equipment from modal
  const handleAddEquipmentFromModal = async (equipment) => {
    if (!equipmentModalSystemId) return;

    try {
      const equipmentName = `${equipment.type} ${equipment.make} ${equipment.model}`.trim();

      const response = await proposalSystemsServices.addEquipment(id, equipmentModalSystemId, {
        modelNumber: equipment.model,
        equipmentName: equipmentName,
        equipmentType: equipment.type,
        manufacturerName: equipment.make,
        priceAtCreation: equipment.price || 0,
        imageUrl: equipment.imageUrl,
        description: equipment.description,
      });

      if (response.status === 200) {
        toast.success("Equipment added");
        loadSystems();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  // Delete equipment
  // Open edit equipment modal
  const handleEditEquipment = (systemId, equipment) => {
    setEditingEquipmentData({
      systemId,
      equipmentId: equipment.id,
      type: equipment.equipmentType,
      make: equipment.manufacturerName,
      model: equipment.modelNumber,
      price: equipment.priceAtCreation,
      imageUrl: equipment.imageUrl,
    });
  };

  // Update equipment from modal
  const handleUpdateEquipmentFromModal = async (equipment) => {
    if (!editingEquipmentData) return;

    try {
      const equipmentName = `${equipment.type} ${equipment.make} ${equipment.model}`.trim();

      const response = await proposalSystemsServices.updateEquipment(
        id,
        editingEquipmentData.systemId,
        editingEquipmentData.equipmentId,
        {
          modelNumber: equipment.model,
          equipmentName: equipmentName,
          equipmentType: equipment.type,
          manufacturerName: equipment.make,
          priceAtCreation: equipment.price || 0,
          imageUrl: equipment.imageUrl,
          description: equipment.description,
        }
      );

      if (response.status === 200) {
        toast.success("Equipment updated");
        loadSystems();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const handleDeleteEquipment = async (systemId, equipmentId, equipmentName) => {
    confirm({
      title: messages.confirm_message.remove,
      description: messages.confirm_message.remove_description(`"${equipmentName}"`),
    })
      .then(async () => {
        try {
          const response = await proposalSystemsServices.removeEquipment(id, systemId, equipmentId);
          if (response.status === 200) {
            toast.success('Equipment removed');
            loadSystems();
          }
        } catch (error) {
          toast.error(messages.errors.error_try_again);
          console.log(error);
        }
      })
      .catch(() => toast.info(messages.cancel_message));
  };

  // Reorder equipment via drag and drop
  const handleEquipmentSortEnd = async (systemId, { oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    
    const system = systems.find(s => s.id === systemId);
    if (!system || !system.equipments) return;
    
    const reorderedEquipments = arrayMoveImmutable(system.equipments, oldIndex, newIndex);
    
    // Update local state immediately for smooth UX
    setSystems(prevSystems => prevSystems.map(s => 
      s.id === systemId ? { ...s, equipments: reorderedEquipments } : s
    ));
    
    // Save order to backend
    try {
      const equipmentIds = reorderedEquipments.map(eq => eq.id);
      await proposalSystemsServices.reorderEquipments(id, systemId, equipmentIds);
    } catch (error) {
      console.log('Error saving equipment order:', error);
      // Reload to restore correct order
      loadSystems();
    }
  };

  // Add scope of work to system
  const handleAddScopeOfWork = async (systemId) => {
    if (!selectedScopeOfWork) {
      toast.error('Please select a scope of work');
      return;
    }

    const work = scopeOfWorkList.find(w => w.id === parseInt(selectedScopeOfWork));
    if (!work) return;

    try {
      const system = systems.find(s => s.id === systemId);
      const currentScopeOfWorks = system.scopeOfWorks || [];
      
      // Check for duplicate
      if (currentScopeOfWorks.some(w => w.id === work.id)) {
        toast.error('This scope of work is already added');
        return;
      }
      
      const newWork = {
        id: work.id,
        description: work.description,
        price: work.price || 0,
      };

      const response = await proposalSystemsServices.updateSystem(id, systemId, {
        scopeOfWorks: [...currentScopeOfWorks, newWork],
      });
      
      if (response.status === 200) {
        toast.success('Scope of Work added');
        setAddingScopeOfWork(null);
        setSelectedScopeOfWork('');
        loadSystems();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  // Delete scope of work from system
  const handleDeleteScopeOfWork = async (systemId, workIndex, workDescription) => {
    confirm({
      title: messages.confirm_message.remove,
      description: messages.confirm_message.remove_description(`"${workDescription}"`),
    })
      .then(async () => {
        try {
          const system = systems.find(s => s.id === systemId);
          const updatedWorks = (system.scopeOfWorks || []).filter((_, idx) => idx !== workIndex);

          const response = await proposalSystemsServices.updateSystem(id, systemId, {
            scopeOfWorks: updatedWorks,
          });
          
          if (response.status === 200) {
            toast.success('Scope of Work removed');
            loadSystems();
          }
        } catch (error) {
          toast.error(messages.errors.error_try_again);
          console.log(error);
        }
      })
      .catch(() => toast.info(messages.cancel_message));
  };

  // Reorder scope of work via drag and drop
  const handleScopeOfWorkSortEnd = async (systemId, { oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    
    const system = systems.find(s => s.id === systemId);
    if (!system || !system.scopeOfWorks) return;
    
    const reorderedWorks = arrayMoveImmutable(system.scopeOfWorks, oldIndex, newIndex);
    
    // Update local state immediately
    setSystems(prevSystems => prevSystems.map(s => 
      s.id === systemId ? { ...s, scopeOfWorks: reorderedWorks } : s
    ));
    
    // Save to backend
    try {
      await proposalSystemsServices.updateSystem(id, systemId, {
        scopeOfWorks: reorderedWorks,
      });
    } catch (error) {
      console.log('Error saving scope of work order:', error);
      loadSystems();
    }
  };

  // Add warranty to system
  const handleAddWarranty = async (systemId) => {
    if (!selectedWarranty) {
      toast.error('Please select a warranty');
      return;
    }

    const warranty = warrantyList.find(w => w.id === parseInt(selectedWarranty));
    if (!warranty) return;

    try {
      const system = systems.find(s => s.id === systemId);
      const currentWarranties = system.warranties || [];
      
      // Check for duplicate
      if (currentWarranties.some(w => w.id === warranty.id)) {
        toast.error('This warranty is already added');
        return;
      }
      
      const newWarranty = {
        id: warranty.id,
        description: warranty.description,
        years: warranty.years,
        price: warranty.price,
      };

      const response = await proposalSystemsServices.updateSystem(id, systemId, {
        warranties: [...currentWarranties, newWarranty],
      });
      
      if (response.status === 200) {
        toast.success('Warranty added');
        setAddingWarranty(null);
        setSelectedWarranty('');
        loadSystems();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  // Delete warranty from system
  const handleDeleteWarranty = async (systemId, warrantyIndex, warrantyDescription) => {
    confirm({
      title: messages.confirm_message.remove,
      description: messages.confirm_message.remove_description(`"${warrantyDescription}"`),
    })
      .then(async () => {
        try {
          const system = systems.find(s => s.id === systemId);
          const updatedWarranties = (system.warranties || []).filter((_, idx) => idx !== warrantyIndex);

          const response = await proposalSystemsServices.updateSystem(id, systemId, {
            warranties: updatedWarranties,
          });
          
          if (response.status === 200) {
            toast.success('Warranty removed');
            loadSystems();
          }
        } catch (error) {
          toast.error(messages.errors.error_try_again);
          console.log(error);
        }
      })
      .catch(() => toast.info(messages.cancel_message));
  };

  // Reorder warranty via drag and drop
  const handleWarrantySortEnd = async (systemId, { oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    
    const system = systems.find(s => s.id === systemId);
    if (!system || !system.warranties) return;
    
    const reorderedWarranties = arrayMoveImmutable(system.warranties, oldIndex, newIndex);
    
    // Update local state immediately
    setSystems(prevSystems => prevSystems.map(s => 
      s.id === systemId ? { ...s, warranties: reorderedWarranties } : s
    ));
    
    // Save to backend
    try {
      await proposalSystemsServices.updateSystem(id, systemId, {
        warranties: reorderedWarranties,
      });
    } catch (error) {
      console.log('Error saving warranty order:', error);
      loadSystems();
    }
  };

  // Handle equipment type change
  const handleEquipmentTypeChange = (type) => {
    const defaultImage = equipmentTypes[type]?.imageUrl || null;
    setNewEquipment({ type, make: '', model: '', price: '', addPicture: true, imageUrl: defaultImage });
  };

  // Handle equipment make change
  const handleEquipmentMakeChange = (make) => {
    setNewEquipment({ ...newEquipment, make, model: '', price: '' });
  };

  // Handle equipment model change
  const handleEquipmentModelChange = (model) => {
    let price = '';
    let imageUrl = newEquipment.imageUrl; // Keep current image as default
    let description = '';
    if (equipmentFilters && newEquipment.type && newEquipment.make && model) {
      const modelData = equipmentFilters[newEquipment.type]?.[newEquipment.make]?.[model];
      if (modelData) {
        const sizes = Object.keys(modelData);
        if (sizes.length > 0) {
          price = modelData[sizes[0]]?.price || '';
          imageUrl = modelData[sizes[0]]?.imageUrl || imageUrl; // Keep current if model has no image
          description = modelData[sizes[0]]?.description || '';
        }
      }
    }
    if (!imageUrl && newEquipment.type && equipmentTypes[newEquipment.type]) {
      imageUrl = equipmentTypes[newEquipment.type]?.imageUrl || newEquipment.imageUrl;
    }
    setNewEquipment({ ...newEquipment, model, price, imageUrl, description });
  };

  // Calculate totals
  const calculateSystemTotal = (system) => {
    const equipmentTotal = (system.equipments || []).reduce((sum, eq) => sum + (parseFloat(eq.priceAtCreation) || 0), 0);
    return equipmentTotal;
  };

  const calculateGrandTotal = (systemsToCalc) => {
    const list = systemsToCalc || systems;
    return list.reduce((sum, system) => sum + calculateSystemTotal(system), 0);
  };

  const calculateOptionTotal = (optionId) => {
    const optionSystems = systems.filter(s => s.optionId === optionId);
    return calculateGrandTotal(optionSystems);
  };

  useEffect(() => {
    loadSystems();
    loadOptions();
    loadEquipmentFilters();
    loadEquipmentTypes();
    loadWarrantyList();
    loadScopeOfWorkList();
  }, [id]);

  if (loading) {
    return <Typography>Loading systems...</Typography>;
  }

  // Render a single system card (reused for both normal and option modes)
  const renderSystemCard = (system) => (
    <Paper key={system.id} sx={{ p: { xs: 1, sm: 1.5, md: 3 }, mb: 2 }} elevation={2}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        {editingSystem === system.id ? (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1, flexWrap: 'wrap' }}>
            <TextField
              label="System Name"
              value={editingSystemData.name}
              onChange={(e) => setEditingSystemData({ ...editingSystemData, name: e.target.value })}
              size="small"
              sx={{ minWidth: 200 }}
            />
            <IconButton color="primary" onClick={() => handleUpdateSystem(system.id, { name: editingSystemData.name })}>
              <SaveIcon />
            </IconButton>
            <IconButton onClick={() => { setEditingSystem(null); setEditingSystemData({ name: '' }); }}>
              <CancelIcon />
            </IconButton>
          </Box>
        ) : (
          <>
            <Typography variant="h6">{system.name}</Typography>
            <Box>
              <Tooltip title="Edit system">
                <IconButton onClick={() => {
                  setEditingSystem(system.id);
                  setEditingSystemData({ name: system.name, installationPrice: system.installationPrice || 0, rebates: system.rebates || 0, instants: system.instants || 0 });
                }}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Copy system">
                <IconButton onClick={() => handleCopySystem(system)}>
                  <FileCopyIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete system">
                <IconButton onClick={() => handleDeleteSystem(system)}>
                  <DeleteForeverIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </>
        )}
      </Box>

      {(() => {
        if (!equipmentFilters || !system.equipments?.length) return null;
        const refs = system.equipments
          .map((eq) => {
            const md = equipmentFilters?.[eq.equipmentType]?.[eq.manufacturerName]?.[eq.modelNumber];
            if (!md) return null;
            const first = Object.values(md)[0];
            return first?.refrigerant && first.refrigerant !== '-' ? first.refrigerant : null;
          })
          .filter(Boolean);
        const unique = [...new Set(refs)];
        if (unique.length <= 1) return null;
        return (
          <Alert severity="warning" sx={{ mb: 1 }}>
            <strong>Refrigerant mismatch!</strong> This system contains equipment with different refrigerants ({unique.join(' & ')}).
            A TXV kit for the evaporator coil may be required for compatibility.
          </Alert>
        );
      })()}

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: isMobile ? 'unset' : 500 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: isMobile ? 24 : 40, p: isMobile ? '2px' : undefined }}></TableCell>
              <TableCell sx={{ fontSize: isMobile ? '0.7rem' : 'inherit', p: isMobile ? '4px' : undefined }}>Type</TableCell>
              {!isMobile && <TableCell>Make</TableCell>}
              <TableCell sx={{ fontSize: isMobile ? '0.7rem' : 'inherit', p: isMobile ? '4px' : undefined }}>Model</TableCell>
              {!isMobile && <TableCell align="center">Image</TableCell>}
              <TableCell align="right" sx={{ width: isMobile ? 55 : 'auto', fontSize: isMobile ? '0.7rem' : 'inherit', p: isMobile ? '4px' : undefined }}>Price</TableCell>
              <TableCell align="center" sx={{ width: isMobile ? 32 : 80, p: isMobile ? '2px' : undefined }}></TableCell>
            </TableRow>
          </TableHead>
          <SortableTableBody useDragHandle onSortEnd={(sortEnd) => handleEquipmentSortEnd(system.id, sortEnd)} helperClass="sortable-row-helper">
            {(system.equipments || []).map((eq, index) => (
              <SortableTableRow key={eq.id} index={index}>
                <TableCell sx={{ width: isMobile ? 24 : 40, p: isMobile ? '2px' : undefined }}><DragHandle /></TableCell>
                <TableCell sx={{ fontSize: isMobile ? '0.7rem' : 'inherit', p: isMobile ? '4px' : undefined }}>
                  {eq.equipmentType || '-'}
                  {isMobile && eq.manufacturerName && (
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.6rem' }}>{eq.manufacturerName}</Typography>
                  )}
                </TableCell>
                {!isMobile && <TableCell>{eq.manufacturerName || '-'}</TableCell>}
                <TableCell sx={{ fontSize: isMobile ? '0.7rem' : 'inherit', p: isMobile ? '4px' : undefined, wordBreak: 'break-word' }}>
                  <Typography component="span" onClick={() => handleEditEquipment(system.id, eq)} sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}>
                    {eq.modelNumber}
                  </Typography>
                </TableCell>
                {!isMobile && (
                  <TableCell align="center">
                    <Tooltip title={eq.imageUrl ? "Click to change image" : "No image"}>
                      <IconButton size="small" onClick={() => eq.imageUrl && openImageDialog({ systemId: system.id, equipmentId: eq.id }, eq.equipmentType)} disabled={!eq.imageUrl} sx={{ p: 0.5 }}>
                        {eq.imageUrl ? <Avatar src={eq.imageUrl} variant="rounded" sx={{ width: 36, height: 36 }} /> : <ImageIcon color="disabled" fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
                <TableCell align="right" sx={{ fontSize: isMobile ? '0.7rem' : 'inherit', p: isMobile ? '4px' : undefined }}>${parseFloat(eq.priceAtCreation || 0).toFixed(2)}</TableCell>
                <TableCell align="center" sx={{ p: isMobile ? '2px' : undefined }}>
                  <IconButton size="small" onClick={() => handleDeleteEquipment(system.id, eq.id, eq.equipmentName)} color="error" sx={{ p: isMobile ? '2px' : undefined }}>
                    <DeleteForeverIcon sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }} />
                  </IconButton>
                </TableCell>
              </SortableTableRow>
            ))}
          </SortableTableBody>
        </Table>
      </TableContainer>

      <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setEquipmentModalSystemId(system.id)} sx={{ mt: 2 }}>
        Add Equipment
      </Button>
    </Paper>
  );

  // Render add-system form for a specific option (or null for no-option mode)
  const renderAddSystemForm = (optionId = null) => (
    <Box sx={{ mb: 2 }}>
      {showAddSystem === (optionId || true) ? (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            label="System Name"
            value={newSystemName}
            onChange={(e) => setNewSystemName(e.target.value)}
            size="small"
            sx={{ minWidth: 250 }}
          />
          <Button variant="contained" onClick={() => handleAddSystem(optionId)} size="small" sx={button('primary')}>
            Add
          </Button>
          <Button variant="outlined" onClick={() => { setShowAddSystem(false); setNewSystemName(''); }} size="small">
            Cancel
          </Button>
        </Box>
      ) : (
        <Button variant="contained" endIcon={<AddIcon />} onClick={() => setShowAddSystem(optionId || true)} size="small" sx={button('secondary')}>
          Add New System
        </Button>
      )}
    </Box>
  );

  return (
    <Fragment>
      {/* Option accepted info */}
      {optionAccepted && hasOptions && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Option accepted. Showing selected equipment.
        </Alert>
      )}

      {/* ===== OPTION MODE ===== */}
      {hasOptions && !optionAccepted ? (
        <>
          {options.map((option, optIdx) => {
            const optionSystems = systems.filter(s => s.optionId === option.id);
            const isLast = optIdx === options.length - 1;

            return (
              <Paper
                key={option.id}
                sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 3, border: '2px solid #0d3c61', borderRadius: 2 }}
                elevation={3}
              >
                {/* Option Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#0d3c61', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {option.name}
                  </Typography>
                  {optIdx > 0 && (
                    <Tooltip title="Delete this option">
                      <IconButton onClick={() => handleDeleteOption(option.id)} color="error">
                        <DeleteForeverIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Systems in this option */}
                {optionSystems.map((system) => renderSystemCard(system))}

                {/* Add system form for this option */}
                {renderAddSystemForm(option.id)}

                {/* Option Equipment Total */}
                <Paper sx={{ p: { xs: 1, sm: 1.5, md: 2 }, mt: 2, backgroundColor: '#e3f2fd' }} elevation={1}>
                  <Typography variant={isMobile ? 'subtitle1' : 'h6'} align="right">
                    {option.name} Total: ${calculateOptionTotal(option.id).toFixed(2)}
                  </Typography>
                </Paper>

                {/* Add Option + button on the last option */}
                {isLast && options.length < 3 && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Button
                      variant="outlined"
                      endIcon={<AddIcon />}
                      onClick={handleAddOption}
                      sx={{ borderColor: '#0d3c61', color: '#0d3c61', fontWeight: 600, '&:hover': { backgroundColor: '#0d3c61', color: '#fff' } }}
                    >
                      Add Option +
                    </Button>
                  </Box>
                )}
              </Paper>
            );
          })}
        </>
      ) : (
        /* ===== NORMAL MODE (no options or option accepted) ===== */
        <>
          {/* Add System Button — hidden when option is accepted */}
          {!optionAccepted && (
            <Box sx={{ mb: 3 }}>
              {renderAddSystemForm()}
            </Box>
          )}

          {/* Systems List — if option accepted, show only selected option's systems */}
          {(() => {
            const displaySystems = optionAccepted && proposal?.selectedOptionId
              ? systems.filter(s => s.optionId === proposal.selectedOptionId)
              : systems;
            return displaySystems.map((system) => renderSystemCard(system));
          })()}

          {/* Equipment Total */}
          {systems.length > 0 && (() => {
            const displaySystems = optionAccepted && proposal?.selectedOptionId
              ? systems.filter(s => s.optionId === proposal.selectedOptionId)
              : systems;
            return (
              <Paper sx={{ p: { xs: 1, sm: 1.5, md: 2 }, mt: 2, backgroundColor: '#e3f2fd' }} elevation={1}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} align="right">
                  Equipment Total: ${calculateGrandTotal(displaySystems).toFixed(2)}
                </Typography>
              </Paper>
            );
          })()}

          {/* Add Option + for PRO users (only in normal mode with systems, no options yet) */}
          {isProUser && !hasOptions && !optionAccepted && systems.length > 0 && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="outlined"
                endIcon={<AddIcon />}
                onClick={handleAddOption}
                sx={{ borderColor: '#0d3c61', color: '#0d3c61', fontWeight: 600, '&:hover': { backgroundColor: '#0d3c61', color: '#fff' } }}
              >
                Add Option +
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Empty State */}
      {systems.length === 0 && !loading && !hasOptions && (
        <Typography color="textSecondary" sx={{ mt: 2 }}>
          No systems added yet. Click "Add New System" to start.
        </Typography>
      )}

      {/* Equipment Select Modal */}
      <EquipmentSelectModal
        open={equipmentModalSystemId !== null || editingEquipmentData !== null}
        onClose={() => { setEquipmentModalSystemId(null); setEditingEquipmentData(null); }}
        onSelect={editingEquipmentData ? handleUpdateEquipmentFromModal : handleAddEquipmentFromModal}
        systemId={equipmentModalSystemId || editingEquipmentData?.systemId}
        editingEquipment={editingEquipmentData}
        isEditMode={editingEquipmentData !== null}
      />

      {/* Image Selection Dialog */}
      <Dialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Select Equipment Image</DialogTitle>
        <DialogContent>
          {imageDialogImages.length === 0 ? (
            <Typography color="textSecondary" sx={{ py: 4, textAlign: 'center' }}>No images available.</Typography>
          ) : (
            <ImageList cols={4} gap={12}>
              {imageDialogImages.map((img, index) => (
                <ImageListItem
                  key={index}
                  onClick={() => handleSelectImage(img)}
                  sx={{ cursor: 'pointer', border: newEquipment.imageUrl === img ? '3px solid' : '2px solid', borderColor: newEquipment.imageUrl === img ? 'primary.main' : 'divider', borderRadius: 1, overflow: 'hidden', '&:hover': { borderColor: 'primary.light', transform: 'scale(1.02)' } }}
                >
                  <img src={img} alt={`Option ${index + 1}`} loading="lazy" style={{ height: 120, objectFit: 'contain', backgroundColor: '#f5f5f5' }} />
                </ImageListItem>
              ))}
            </ImageList>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default ProposalSystemTableNew;
